/**
 * AdSense Configuration Checker
 * 
 * Checks if AdSense configuration matches what's in AdSense Dashboard.
 * Run this in browser console: checkAdSenseConfig()
 */

export const checkAdSenseConfig = () => {
  const results: {
    check: string;
    status: '✅ PASS' | '❌ FAIL' | '⚠️ WARNING';
    message: string;
    action?: string;
  }[] = [];

  console.group('🔍 AdSense Configuration Check');

  // Check Publisher ID
  const expectedPublisherId = 'ca-pub-5882867309114563';
  const script = document.querySelector('script[src*="pagead2.googlesyndication.com"]') as HTMLScriptElement;
  
  if (script) {
    const scriptSrc = script.src;
    const publisherIdInUrl = scriptSrc.match(/client=([^&]+)/)?.[1];
    
    if (publisherIdInUrl === expectedPublisherId) {
      results.push({
        check: 'Publisher ID in Script URL',
        status: '✅ PASS',
        message: `Publisher ID matches: ${publisherIdInUrl}`
      });
      console.log('✅ Publisher ID in URL:', publisherIdInUrl);
    } else {
      results.push({
        check: 'Publisher ID in Script URL',
        status: '❌ FAIL',
        message: `Publisher ID mismatch. Expected: ${expectedPublisherId}, Found: ${publisherIdInUrl || 'none'}`,
        action: 'Check src/config/adsense.ts - PUBLISHER_ID should be ca-pub-5882867309114563'
      });
      console.error('❌ Publisher ID mismatch:', publisherIdInUrl, 'Expected:', expectedPublisherId);
    }
  } else {
    results.push({
      check: 'Publisher ID in Script URL',
      status: '❌ FAIL',
      message: 'Script not found - cannot verify Publisher ID',
      action: 'Script may not be loaded yet or was blocked'
    });
    console.error('❌ Script not found');
  }

  // Check Slot IDs
  const adElements = document.querySelectorAll('.adsbygoogle');
  const expectedSlotIds = {
    '5945206258': 'IN_ARTICLE_1',
    '3214019569': 'IN_ARTICLE_2',
    '7996654521': 'SIDEBAR',
    '2005961242': 'FOOTER'
  };

  if (adElements.length > 0) {
    console.log(`Found ${adElements.length} ad element(s)`);
    
    adElements.forEach((el, index) => {
      const slotId = el.getAttribute('data-ad-slot');
      const clientId = el.getAttribute('data-ad-client');
      
      if (slotId && expectedSlotIds[slotId as keyof typeof expectedSlotIds]) {
        results.push({
          check: `Ad ${index + 1} Slot ID`,
          status: '✅ PASS',
          message: `Slot ID ${slotId} (${expectedSlotIds[slotId as keyof typeof expectedSlotIds]}) is valid`
        });
        console.log(`✅ Ad ${index + 1}: Slot ${slotId} (${expectedSlotIds[slotId as keyof typeof expectedSlotIds]})`);
      } else if (slotId) {
        results.push({
          check: `Ad ${index + 1} Slot ID`,
          status: '⚠️ WARNING',
          message: `Slot ID ${slotId} is not in expected list`,
          action: 'Verify this slot ID exists in AdSense Dashboard'
        });
        console.warn(`⚠️ Ad ${index + 1}: Slot ${slotId} not in expected list`);
      } else {
        results.push({
          check: `Ad ${index + 1} Slot ID`,
          status: '❌ FAIL',
          message: 'Slot ID missing',
          action: 'Check ad component configuration'
        });
        console.error(`❌ Ad ${index + 1}: Slot ID missing`);
      }

      if (clientId !== expectedPublisherId) {
        results.push({
          check: `Ad ${index + 1} Client ID`,
          status: '❌ FAIL',
          message: `Client ID mismatch. Expected: ${expectedPublisherId}, Found: ${clientId || 'none'}`,
          action: 'Check ad component - data-ad-client should match Publisher ID'
        });
        console.error(`❌ Ad ${index + 1}: Client ID mismatch`);
      }
    });
  } else {
    results.push({
      check: 'Ad Elements',
      status: '❌ FAIL',
      message: 'No ad elements found',
      action: 'Check if AdFooter, AdSidebar components are rendered'
    });
    console.error('❌ No ad elements found');
  }

  // Summary
  console.group('📊 Summary');
  const passed = results.filter(r => r.status === '✅ PASS').length;
  const failed = results.filter(r => r.status === '❌ FAIL').length;
  const warnings = results.filter(r => r.status === '⚠️ WARNING').length;
  
  console.log(`✅ Passed: ${passed}`);
  console.log(`❌ Failed: ${failed}`);
  console.log(`⚠️ Warnings: ${warnings}`);
  
  if (failed > 0) {
    console.group('❌ Failed Checks');
    results.filter(r => r.status === '❌ FAIL').forEach(check => {
      console.error(`${check.check}: ${check.message}`);
      if (check.action) console.log(`   💡 ${check.action}`);
    });
    console.groupEnd();
  }
  
  console.groupEnd();
  console.groupEnd();

  return results;
};

// Make it available globally
if (typeof window !== 'undefined') {
  (window as any).checkAdSenseConfig = checkAdSenseConfig;
}

